import { isAxiosError } from 'axios';
import request from '../utils/axios/request';
import maskPhone from '../utils/mask/phone';
import resetFormat from '../utils/reset';
import { PaginationContent } from '../utils/types/pagination';
import { Query } from '../utils/types/query';
import { IClient } from './Client.service';
import { ICarrier } from './Carrier.service';
import { IYard } from './Yard.service';
import UserService, { IUser } from './User.service';

export interface IContact {
	id?: number;
	name: string;
	phone: string;
	email: string;
	isActive: boolean;
	client: Partial<IClient>;
	carriers: Partial<ICarrier>[];
	yards: Partial<IYard>[];
	createdAt?: Date;
	updatedAt?: Date;
	actions?: string;
	type?: 'Shipowner' | 'Client';
}

export type IContatoAdministrativo = (IUser | IContact) & {
	origem?: 'users' | 'contacts';
};

abstract class ContactService {
	static async get(id: number) {
		try {
			const data = await request(
				`/contacts/client/${id}`,
				'get',
				{},
				'client'
			);
			return data as IContact;
		} catch (error: any) {}
	}
	static async getAll(params: Query) {
		let isActive;
		if (params?.isActive && Number(params.isActive) > -1) {
			isActive = params.isActive;
		}
		try {
			const response: PaginationContent<IContact> = await request(
				'/contacts',
				'get',
				{
					params: { ...params, isActive },
				}
			);
			return response;
		} catch (error: any) {}
	}

	static async listarComUsuarios(parametros: {
		pageCurrent: number;
		pageSize: number;
		account: string;
		idClient: number;
	}): Promise<PaginationContent<IContatoAdministrativo> | undefined> {
		const usuarios = await UserService.getAll(parametros);
		if (!usuarios) return;

		const inicioContatos = Math.max(
			0,
			(parametros.pageCurrent - 1) * parametros.pageSize - usuarios.totalItems
		);
		const paginaContatos = Math.floor(inicioContatos / parametros.pageSize) + 1;
		const contatos = await this.getAll({
			pageSize: parametros.pageSize,
			pageCurrent: paginaContatos,
		});
		if (!contatos) return;

		const quantidadeContatos = parametros.pageSize - usuarios.data.length;
		let contatosDaPagina = contatos.data.slice(
			inicioContatos % parametros.pageSize
		);
		if (
			contatosDaPagina.length < quantidadeContatos &&
			paginaContatos < contatos.totalPages
		) {
			const proximosContatos = await this.getAll({
				pageSize: parametros.pageSize,
				pageCurrent: paginaContatos + 1,
			});
			if (!proximosContatos) return;
			contatosDaPagina = [...contatosDaPagina, ...proximosContatos.data];
		}

		const totalItems = usuarios.totalItems + contatos.totalItems;
		return {
			data: [
				...usuarios.data.map((usuario) => ({
					...usuario,
					origem: 'users' as const,
				})),
				...contatosDaPagina.slice(0, quantidadeContatos).map((contato) => ({
					...contato,
					origem: 'contacts' as const,
				})),
			],
			totalItems,
			totalPages: Math.max(1, Math.ceil(totalItems / parametros.pageSize)),
			pageCurrent: parametros.pageCurrent,
		};
	}

	static async create(
		data: IContact,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'post');
	}

	static async edit(
		data: IContact,
		setError: React.Dispatch<React.SetStateAction<string>>
	) {
		return this.createOrEdit(data, setError, 'patch');
	}

	private static async createOrEdit(
		data: IContact,
		setError: React.Dispatch<React.SetStateAction<string>>,
		mode: 'post' | 'patch'
	) {
		try {
			const {
				id,
				client,
				yards,
				carriers,
				createdAt,
				updatedAt,
				...contact
			} = data;
			const idYards = yards.map((value) => value.id);
			const idCarriers = carriers.map((value) => value.id);
			const url = mode === 'post' ? '/contacts' : `/contacts/${id}`;
			const response = await request(url, mode, {
				data: {
					...contact,
					idYards,
					idCarriers,
					id_cliente: Number(client.id),
				},
			});
			return response;
		} catch (error) {
			if (isAxiosError(error) && error.status === 409)
				setError('alert.duplicated_email');
			else setError('alert.error.generic');
		}
	}

	static async delete(id: number) {
		const response = await request(`/contacts/${id}`, 'delete');
		return response;
	}
}

export default ContactService;
